import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Wrench, Monitor, AlertTriangle, ArrowRight } from 'lucide-react';

const SOFTWARE_OPTIONS = [
  'Fusion 360',
  'Vectric Aspire',
  'Vectric VCarve',
  'Carbide Create',
  'GRBL (Candle/UGS)',
  'Mach3',
  'Mach4',
  'LinuxCNC',
  'UCCNC',
  'Other',
];

const TroubleshootingHub = () => {
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [machineId, setMachineId] = useState('');
  const [software, setSoftware] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/machines`);
        setMachines(res.data || []);
        if (res.data?.length && !machineId) setMachineId(res.data[0].id);
      } catch (e) {
        console.error('Failed to load machines for troubleshooting hub', e);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedMachine = useMemo(
    () => machines.find((m) => m.id === machineId) || null,
    [machines, machineId]
  );

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.ROUTER_TROUBLESHOOTING)}
      data-testid="troubleshooting-hub"
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Page Header */}
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Diagnose</div>
        <h1 
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#e67e22] tracking-tight uppercase"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          Troubleshooting Hub
        </h1>
        <p className="mt-2 text-sm md:text-base text-[#e67e22] max-w-lg">
          Start here when something goes wrong. Choose your machine or software and get AI-guided troubleshooting.
        </p>
      </div>

      <div className="max-w-5xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Machine Issues Card */}
          <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-6 hover:border-[#27ae60]/50 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center rounded-sm">
                <Wrench className="w-5 h-5 text-[#27ae60]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Machine Issues</h3>
                <p className="text-xs text-[#a3a3a3]">Vibration, chatter, burning, accuracy problems</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-xs text-[#737373] uppercase tracking-wide">Select machine</div>
                <Select value={machineId} onValueChange={setMachineId}>
                  <SelectTrigger className="bg-[#1f1f1f] border-[#3a3a3a] text-white">
                    <SelectValue placeholder={machines.length ? 'Select machine' : 'No machines found'} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#242424] border-[#3a3a3a]">
                    {machines.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="text-white hover:bg-[#3a3a3a]">
                        {m.name || 'Machine'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="w-full bg-[#27ae60] hover:bg-[#219a52] text-white font-bold"
                disabled={!machineId}
                data-testid="troubleshoot-machine-ask-ai"
                onClick={() => navigate(`/troubleshooting/ask-ai?machine_id=${encodeURIComponent(machineId)}`)}
              >
                Ask AI about this machine
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Software Issues Card */}
          <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-6 hover:border-[#3498db]/50 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center rounded-sm">
                <Monitor className="w-5 h-5 text-[#3498db]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Software / Workflow Issues</h3>
                <p className="text-xs text-[#a3a3a3]">Toolpaths, CAM settings, post-processing</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-xs text-[#737373] uppercase tracking-wide">Select software</div>
                <Select value={software} onValueChange={setSoftware}>
                  <SelectTrigger className="bg-[#1f1f1f] border-[#3a3a3a] text-white">
                    <SelectValue placeholder="Select software" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#242424] border-[#3a3a3a]">
                    {SOFTWARE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} className="text-white hover:bg-[#3a3a3a]">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="w-full bg-[#3498db] hover:bg-[#2980b9] text-white font-bold"
                disabled={!software}
                data-testid="troubleshoot-software-ask-ai"
                onClick={() => navigate(`/troubleshooting/ask-ai?software=${encodeURIComponent(software)}`)}
              >
                Ask AI about this software
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Tip */}
        <div className="bg-[#242424] border border-[#f39c12]/30 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#f39c12] mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-white mb-1">Quick tip</h3>
              <p className="text-sm text-[#a3a3a3]">
                For best results, keep your Machine Setup updated — the troubleshooting chat will use your machine's
                motors, drive type, guides, spindle, controller, rigidity and axis count when suggesting safe fixes.
              </p>
              <Link 
                to="/settings/machine-setup" 
                className="inline-flex items-center gap-1 mt-2 text-sm text-[#e67e22] hover:text-[#f39c12] transition-colors"
              >
                Review Machine Setup
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TroubleshootingHub;
